<template>
  <div class="p-3 pt-5 rounded bg-body mb-5">
    <div class="row align-items-end mb-5">
      <div class="col-lg-2 mb-5 mb-lg-0">
        <date-field
          v-model="filters.fromDate"
          id="ordersWeek.fromDate"
          name="ordersWeek.fromDate"
          placeholder="Дата с"
        />
      </div>
      <div class="col-lg-2 mb-5 mb-lg-0">
        <date-field
          v-model="filters.toDate"
          id="ordersWeek.toDate"
          name="ordersWeek.toDate"
          placeholder="Дата по"
        />
      </div>
      <div class="col-lg-3 ms-auto d-flex justify-content-end gap-2 mb-5 mb-lg-0">
        <button type="button" class="btn btn-sm btn-primary" @click="applyFilters">
          Показать
        </button>
        <button type="button" class="btn btn-sm btn-light-danger" @click="resetFilters">
          Сбросить
        </button>
      </div>
    </div>
    <loader v-if="loaded" :height="25" />
    <div v-show="!loaded">
      <apexchart
        type="area"
        height="350"
        :options="chartOptions"
        :series="series"
      />
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts'
import Loader from '@/components/Loader'
import DateField from '@/components/Fields/DateField'
import axios from 'axios'

export default {
  name: 'OrdersWeek',
  components: {
    Loader,
    DateField,
    apexchart: VueApexCharts
  },
  data() {
    return {
      loaded: false,
      defaultFromDate: '',
      defaultToDate: '',
      filters: {
        fromDate: '',
        toDate: ''
      },
      series: [],
      chartOptions: {
        chart: {
          type: 'area',
          height: 350,
          toolbar: { show: false },
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        dataLabels: {
          enabled: true,
          formatter: val => (val > 0 ? val : ''),
          style: {
            fontSize: '12px',
            colors: ['#333']
          },
          background: {
            enabled: true,
            foreColor: '#fff',
            borderRadius: 4,
            padding: 2,
            opacity: 0.9
          }
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.05,
            stops: [0, 90, 100]
          }
        },
        colors: ['#00C851', '#ff4444'], // зелёный и красный
        xaxis: {
          categories: [],
          title: { text: 'Дата' },
          labels: {
            style: { fontSize: '12px' }
          }
        },
        yaxis: {
          title: { text: 'Количество заказов' },
          labels: {
            formatter: val => parseInt(val),
            style: { fontSize: '12px' }
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'center',
          fontSize: '13px'
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: val => `${parseInt(val)} заказов`
          }
        },
        title: {
          text: 'Завершённые и отменённые заказы за неделю',
          align: 'left',
          style: {
            fontSize: '16px',
            fontWeight: '600'
          }
        },
        grid: {
          padding: { left: 10, right: 10 },
        }
      }
    }
  },
  mounted() {
    this.setDefaultDates()
    this.getOrdersWeek()
  },
  methods: {
    formatDateForInput(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    setDefaultDates() {
      // дефолт: последние 7 дней включительно (как было)
      const to = new Date()
      const from = new Date(to)
      from.setDate(to.getDate() - 6)

      this.defaultFromDate = this.formatDateForInput(from)
      this.defaultToDate = this.formatDateForInput(to)

      this.filters.fromDate = this.defaultFromDate
      this.filters.toDate = this.defaultToDate
    },
    normalizeRange(fromDateStr, toDateStr) {
      const fromStr = fromDateStr || this.defaultFromDate
      const toStr = toDateStr || this.defaultToDate

      const from = new Date(`${fromStr}T00:00:00`)
      const to = new Date(`${toStr}T23:59:59.999`)

      let fromDate = from
      let toDate = to
      if (fromDate.getTime() > toDate.getTime()) {
        const tmp = fromDate
        fromDate = toDate
        toDate = tmp
      }

      return { fromDate, toDate }
    },
    formatDate(ts) {
      const d = new Date(ts)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      return `${day}.${month}`
    },
    formatAxisDate(dateValue) {
      if (!dateValue) return ''

      let d
      if (typeof dateValue === 'number') {
        d = new Date(dateValue)
      } else if (typeof dateValue === 'string') {
        if (/^\d+$/.test(dateValue)) {
          d = new Date(Number(dateValue))
        } else {
          const parsed = new Date(dateValue)
          d = isNaN(parsed.getTime()) ? null : parsed
        }
      }

      if (!d) return String(dateValue)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      return `${day}.${month}`
    },
    applyFilters() {
      this.getOrdersWeek()
    },
    resetFilters() {
      this.filters.fromDate = this.defaultFromDate
      this.filters.toDate = this.defaultToDate
      this.getOrdersWeek()
    },
    getOrdersWeek() {
      this.loaded = true

      const { fromDate: from, toDate: to } = this.normalizeRange(
        this.filters.fromDate,
        this.filters.toDate
      )

      const fromDate = from.getTime()
      const toDate = to.getTime()

      axios.get('order-service/api/v1/admin/statistic/esp', {
        params: { fromDate, toDate }
      })
        .then(response => {
          const data = Array.isArray(response.data) ? response.data : []

          if (!data.length) {
            console.warn('Нет данных для построения графика')
            this.series = []
            this.chartOptions.xaxis.categories = []
            return
          }

          const categories = data.map(row => this.formatAxisDate(row?.date))

          const series = [
            {
              name: 'Завершённые',
              data: data.map(row => row?.finish?.count ?? 0)
            },
            {
              name: 'Отменённые',
              data: data.map(row => row?.cancelled?.count ?? 0)
            }
          ]

          // 🟢 Формируем заголовок с датами
          const titleText = `Завершённые и отменённые заказы с ${this.formatDate(fromDate)} по ${this.formatDate(toDate)}`

          this.series = series
          this.chartOptions = {
            ...this.chartOptions,
            xaxis: { ...this.chartOptions.xaxis, categories },
            title: { ...this.chartOptions.title, text: titleText }
          }
        })
        .catch(err => {
          console.error('Ошибка при запросе getOrdersWeek:', err)
        })
        .finally(() => {
          this.loaded = false
        })
    }
  }
}
</script>
